import React, { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  Monitor, Image, ListVideo, Calendar, Radio, Type,
  BarChart2, Bell, Settings, LogOut, ChevronLeft,
  ChevronRight, Wifi, Shield, Key, Webhook, Layers,
  Users, Building2, Activity, Cpu, X, CheckCircle,
  AlertTriangle, Info, AlertCircle
} from 'lucide-react'
import { useAuthStore, useAlertStore } from '@/stores/stores'

// ══════════════════════════════════════════════════════════════
// SIDEBAR
// ══════════════════════════════════════════════════════════════
export function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuthStore()
  const { unseenCount }  = useAlertStore()
  const navigate = useNavigate()

  const nav = [
    {
      section: 'Přehled',
      items: [
        { to: '/dashboard',  icon: Activity,   label: 'Dashboard' },
        { to: '/screens',    icon: Monitor,    label: 'Obrazovky' },
        { to: '/alerts',     icon: Bell,       label: 'Alerty', badge: unseenCount > 0 ? unseenCount : null },
      ]
    },
    {
      section: 'Obsah',
      items: [
        { to: '/media',      icon: Image,      label: 'Média' },
        { to: '/playlists',  icon: ListVideo,  label: 'Playlisty' },
        { to: '/schedules',  icon: Calendar,   label: 'Plánování' },
        { to: '/tickers',    icon: Type,       label: 'Tickery' },
        { to: '/streams',    icon: Radio,      label: 'Streamy' },
      ]
    },
    {
      section: 'Analytika',
      items: [
        { to: '/statistics',  icon: BarChart2, label: 'Statistiky' },
        { to: '/screen-stats', icon: BarChart2, label: 'Statistiky obrazovek' },
      ]
    },
    {
      section: 'Systém',
      items: [
        { to: '/api-keys',   icon: Key,        label: 'API klíče' },
        { to: '/webhooks',   icon: Webhook,    label: 'Webhooky' },
        { to: '/settings',   icon: Settings,   label: 'Nastavení' },
        ...(user?.role === 'superadmin' ? [
          { to: '/tenants',  icon: Building2,  label: 'Tenanti' },
          { to: '/users',    icon: Users,      label: 'Uživatelé' },
          { to: '/system',   icon: Cpu,        label: 'Systém' },
        ] : [])
      ]
    },
  ]

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <aside className="sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="logo-icon">
          <Layers size={16} color="white" />
        </div>
        {!collapsed && <span>SIGNAGE</span>}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {nav.map(section => (
          <div key={section.section}>
            {!collapsed && (
              <div className="sidebar-section-label">{section.section}</div>
            )}
            {section.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="nav-icon" size={16} />
                {!collapsed && <span>{item.label}</span>}
                {!collapsed && item.badge && (
                  <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border)' }}>
        {!collapsed && (
          <div style={{ padding: '8px 10px', marginBottom: 4 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{user?.name}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              {user?.role}
            </div>
          </div>
        )}
        <button
          className="nav-item"
          style={{ width: '100%', color: 'var(--red)' }}
          onClick={handleLogout}
          title={collapsed ? 'Odhlásit' : undefined}
        >
          <LogOut size={16} className="nav-icon" />
          {!collapsed && <span>Odhlásit se</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        style={{
          position: 'absolute', top: 64, right: -12,
          width: 24, height: 24,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-secondary)',
          zIndex: 10,
        }}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  )
}

// ══════════════════════════════════════════════════════════════
// TOPBAR
// ══════════════════════════════════════════════════════════════
export function Topbar({ title, subtitle, actions }) {
  const { unseenCount } = useAlertStore()

  return (
    <header className="app-topbar">
      <div style={{ flex: 1 }}>
        {title && (
          <div className="page-title" style={{ fontSize: '1rem' }}>{title}</div>
        )}
        {subtitle && (
          <div className="page-subtitle" style={{ marginTop: 0 }}>{subtitle}</div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {actions}

        {/* Alert bell */}
        <NavLink
          to="/alerts"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
          }}
          className="btn btn-ghost btn-icon"
        >
          <Bell size={16} />
          {unseenCount > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 4,
              width: 8, height: 8,
              background: 'var(--red)',
              borderRadius: '50%',
            }} />
          )}
        </NavLink>
      </div>
    </header>
  )
}

// ══════════════════════════════════════════════════════════════
// APP SHELL
// ══════════════════════════════════════════════════════════════
export function AppShell({ children }) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className={`app-shell ${collapsed ? 'collapsed' : ''}`}>
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
      <div style={{ display: 'contents' }}>
        {children}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// TOAST CONTAINER
// ══════════════════════════════════════════════════════════════
const toastIcons = {
  success: <CheckCircle size={16} color="var(--green)" />,
  error:   <AlertCircle size={16} color="var(--red)" />,
  warning: <AlertTriangle size={16} color="var(--yellow)" />,
  info:    <Info size={16} color="var(--accent-light)" />,
}

export function ToastContainer() {
  const { toasts, removeToast } = useAlertStore()

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24,
      display: 'flex', flexDirection: 'column', gap: 10,
      zIndex: 9999, maxWidth: 360,
    }}>
      {toasts.map(toast => (
        <div
          key={toast.id}
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-lg)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            boxShadow: 'var(--shadow-lg)',
            animation: 'slideUp 0.2s ease',
          }}
        >
          {toastIcons[toast.type] ?? toastIcons.info}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{toast.title}</div>
            {toast.message && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                {toast.message}
              </div>
            )}
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            style={{ color: 'var(--text-muted)', flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// MODAL
// ══════════════════════════════════════════════════════════════
export function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  if (!open) return null

  const maxWidths = { sm: 400, md: 560, lg: 720, xl: 900 }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: maxWidths[size] }}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// CONFIRM DIALOG
// ══════════════════════════════════════════════════════════════
export function ConfirmDialog({ open, onClose, onConfirm, title, message, danger }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title ?? 'Potvrdit akci'}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Zrušit</button>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => { onConfirm(); onClose() }}
          >
            Potvrdit
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--text-secondary)' }}>{message}</p>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════
// STATUS BADGE
// ══════════════════════════════════════════════════════════════
export function StatusBadge({ status }) {
  const map = {
    online:   'badge-online',
    offline:  'badge-offline',
    pending:  'badge-pending',
    active:   'badge-online',
    disabled: 'badge-offline',
    ready:    'badge-online',
    error:    'badge-offline',
    processing: 'badge-info',
    done:     'badge-online',
    failed:   'badge-offline',
  }
  const labels = {
    online: 'Online', offline: 'Offline', pending: 'Čeká',
    active: 'Aktivní', disabled: 'Zakázán', ready: 'Připraven',
    error: 'Chyba', processing: 'Zpracovávám', done: 'Hotovo', failed: 'Selhalo',
  }

  return (
    <span className={`badge ${map[status] ?? 'badge-info'}`}>
      {labels[status] ?? status}
    </span>
  )
}

// ══════════════════════════════════════════════════════════════
// LOADING SPINNER
// ══════════════════════════════════════════════════════════════
export function Spinner({ size = 18 }) {
  return <div className="spinner" style={{ width: size, height: size }} />
}

export function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <Spinner size={32} />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// SEARCH BAR
// ══════════════════════════════════════════════════════════════
export function SearchBar({ value, onChange, placeholder = 'Hledat...' }) {
  return (
    <input
      type="text"
      className="form-input"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ maxWidth: 280 }}
    />
  )
}

// ══════════════════════════════════════════════════════════════
// EMPTY STATE
// ══════════════════════════════════════════════════════════════
export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="empty-state">
      {Icon && <Icon size={40} color="var(--text-muted)" />}
      <div className="empty-state-title">{title}</div>
      {text && <div className="empty-state-text">{text}</div>}
      {action}
    </div>
  )
}
